import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { formatIfDefined, loadRawEntityDataByQuery } from '@adornis/bh-util/util.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { EntityPickerOptions } from '@adornis/entity-picker/client/entity-picker-options.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { html, type TemplateResult } from 'lit';
import { ParticipationState } from '../enums/enums.js';
import { Participation } from './Participation.js';
import { ProductEntryRequirement } from './ProductEntryRequirement.js';
import { Workshop } from './workshop.js';

function renderEntityPickerItem(name: string, timeframeName: Maybe<string>, startDate: Maybe<string>) {
  return html`
    <x-flex
      ${css({
        padding: `10px 20px`,
        cursor: 'pointer',
      })}
      space="md"
    >
      <x-flex horizontal ${css({ gap: '10px' })}>
        <x-text> ${name} </x-text>
        <x-text ${css({ opacity: 0.6 })}><i> ${timeframeName} </i></x-text>
        <x-text ${css({ opacity: 0.6 })}><i> ${startDate} </i></x-text>
      </x-flex>
    </x-flex>
  `;
}

@Entity()
export class ProductEntryRequirementForParticipation extends ProductEntryRequirement {
  public static override _class = 'ProductEntryRequirementForParticipation';

  @Field(type => String)
  @validate(nonOptional())
  requiredProductID?: string;

  override hasValue() {
    return !!this.requiredProductID;
  }

  override renderEditUI(): TemplateResult<1> {
    return html`
      <x-entity-picker
        placeholder="Produkte auswählen..."
        .options=${
          {
            class: Workshop,
            filter: new AdornisFilter({
              searchFields: ['name'],
              aspects: [new StringAspect({ fieldName: 'institutionID', mode: 'equals', value: this.institutionID })],
            }),
            limit: 200,
            selectionSet: { name: 1, timeframe: { name: 1 }, appointments: { start: 1 } },
          } satisfies EntityPickerOptions<typeof Workshop>
        }
        .renderString=${(workshop: Maybe<Workshop>) => workshop?.name}
        .renderItem=${workshop => {
          return renderEntityPickerItem(
            workshop?.name,
            workshop?.timeframe?.name,
            formatIfDefined(workshop?.appointments?.start, 'dd.MM.yyyy'),
          );
        }}
        .value=${this.requiredProductID}
        @value-picked=${(e: ValueEvent<string>) => (this.requiredProductID = e.detail.value)}
      ></x-entity-picker>
    `;
  }

  override async requirementIsMet(userID: string) {
    const participatedParticipations = await loadRawEntityDataByQuery(
      Participation,
      {
        userID,
        state: ParticipationState.participated,
      },
      { productID: 1 },
    );
    const participatedProductIDs = participatedParticipations.map(p => p.productID);
    const requiredProductID = this.requiredProductID;
    if (!requiredProductID) return true;
    return participatedProductIDs.includes(requiredProductID);
  }

  override async label() {
    if (!this.requiredProductID) return ['---', '---'] satisfies [string, string];
    // Labels are also rendered in the client, where the server read helper cannot run.
    const { getProductByID } = await import('../api/product.js');
    const requiredProduct = await getProductByID(this.requiredProductID)({ name: 1 });
    if (!requiredProduct) throw new Error('product not found ' + this.requiredProductID);
    return [
      'erfordert Teilnahme: ' + requiredProduct.name,
      'requires participation: ' + requiredProduct.name,
    ] satisfies [string, string];
  }
}
